import React, { useState } from 'react';
import { X, User, Mail, Phone, Instagram, ArrowLeft, CheckCircle, Lock, AlertCircle } from 'lucide-react';

const MyProfilePage = ({ userProfile, onClose, onUpdate, loading, error }) => {
  const [formData, setFormData] = useState({
    name: userProfile?.name || '',
    family: userProfile?.familyName || userProfile?.family || '',
    email: userProfile?.identifiers?.email || userProfile?.email || '',
    phone: userProfile?.identifiers?.phone || userProfile?.phone || '',
    instagramUsername: userProfile?.identifiers?.instagramUsername || userProfile?.instagramUsername || '',
    password: '', // Only for changing password
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'First name is required';
    if (!formData.family.trim()) errors.family = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.instagramUsername.trim()) errors.instagramUsername = 'Instagram username is required';
    if (formData.password && formData.password.length < 8) errors.password = 'Password must be at least 8 characters';
    if (formData.password && formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    if (validateForm()) {
      const result = await onUpdate(formData);
      if (result.success) {
        setSuccess(true);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section for User Reviews */}
      <section className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              My Profile
              <span className="block bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                Update or change your profile details
              </span>
            </h1>
            <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
              Here you can view and update your personal information, contact details, and Instagram username. Keeping your profile up to date helps you get the most out of our platform and ensures you stay connected.
            </p>
          </div>
        </div>
      </section>
      <section className="py-8 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="inset-0 z-50  bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="text-center flex-1">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-2">
                    <User className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">My Profile</h2>
                  <p className="text-gray-600 text-sm mt-1">Update your account details</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              {success && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700 text-sm">Profile updated successfully!</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${formErrors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      placeholder="John"
                    />
                    {formErrors.name && (
                      <p className="text-red-600 text-xs mt-1">{formErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.family}
                      onChange={(e) => handleInputChange('family', e.target.value)}
                      className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${formErrors.family ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      placeholder="Doe"
                    />
                    {formErrors.family && (
                      <p className="text-red-600 text-xs mt-1">{formErrors.family}</p>
                    )}
                  </div>
                </div>
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="inline w-4 h-4 mr-1" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${formErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="john.doe@example.com"
                  />
                  {formErrors.email && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.email}</p>
                  )}
                </div>
                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="inline w-4 h-4 mr-1" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${formErrors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="+1 (555) 123-4567"
                  />
                  {formErrors.phone && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.phone}</p>
                  )}
                </div>
                {/* Instagram Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Instagram className="inline w-4 h-4 mr-1" />
                    Instagram Username *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500 pointer-events-none">@</span>
                    <input
                      type="text"
                      value={formData.instagramUsername}
                      onChange={(e) => handleInputChange('instagramUsername', e.target.value.replace('@', ''))}
                      className={`w-full pl-8 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${formErrors.instagramUsername ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                      placeholder="yourusername"
                    />
                  </div>
                  {formErrors.instagramUsername && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.instagramUsername}</p>
                  )}
                </div>
                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="inline w-4 h-4 mr-1" />
                    New Password
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${formErrors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Change your password (optional)"
                  />
                  {formErrors.password && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.password}</p>
                  )}
                </div>
                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="inline w-4 h-4 mr-1" />
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${formErrors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Confirm your new password"
                  />
                  {formErrors.confirmPassword && (
                    <p className="text-red-600 text-xs mt-1">{formErrors.confirmPassword}</p>
                  )}
                </div>
                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="text-red-600 mt-0.5" size={16} />
                      <div>
                        <p className="text-red-800 text-sm">{error}</p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Updating Profile...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle size={16} />
                      Update Profile
                    </div>
                  )}
                </button>
              </form>
            </div>
            {/* Benefits Section */}
            <div className="p-6 bg-gray-50 border-t border-gray-200">
              <h3 className="font-semibold text-gray-800 mb-3">Profile Benefits</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-green-600" />
                  Keep your contact info up to date
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-green-600" />
                  Get personalized recommendations
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-green-600" />
                  Build your reviewer reputation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-green-600" />
                  Connect with service providers
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyProfilePage;
